import { useEffect, useRef } from 'react';
import { batch, useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import 'core-js/features/url-search-params';
import 'core-js/features/object/from-entries';
import { storeAvailableFilters } from '@common/Filters/actions/filtersActions';
import { useConfig } from '@common/Hooks/useConfig';
import { get } from '@common/Http';
import {
    setQueryFromUrl,
    setProducts,
    setSeoData,
    updateProductsCount
} from '@common/Listing/actions/listingActions';
import { getQueryFromUrl } from '@common/Listing/helpers/filters';
import { updatePagination } from '@common/Pagination/actions/paginationActions';
import { LISTING_API_GET_LISTING_BY_NAVIGATION_ID } from '@routes';
import { getNewLocationSearch } from '../helpers/listingPageRouter';
import { applySeoData } from '../helpers/seo';

export function ListingPageQueryListener({
    baseNavigationId,
    onProductsChange
}) {
    const config = useConfig();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const firstCall = useRef(true);
    const locationChangeRef = useRef(true);
    const locationChangeFromPopStateRef = useRef();

    const query = useSelector(state => state.listing.query);
    const limit = useSelector(state => state.listing.limit);
    const navigationId = useSelector(state => state.listing.navigationId);
    const listingPaging = useSelector(state => state.listing.paging);

    const isIndexableFiltersRemovalFeatureActive = config.isActiveFeature('PLT-36433_default_and_indexable_filters_removal');
    const isNewListingsCanonicalTagFeatureActive = config.isActiveFeature('PLT-44640_new_listings_canonical_tag');
    const isNextPrevAttributesFeatureActive = config.isActiveFeature('PLT-44638_prev_next_attributes');

    const { productsPerPage } = listingPaging || {};

    const updateUrl = (requestParameters, seoData) => {
        const { url_path: seoUrlPath } = seoData || {};

        navigate([
            seoUrlPath,
            getNewLocationSearch({
                isIndexableFiltersRemovalFeatureActive,
                seoUrlPath,
                requestParameters,
                productsPerPage
            })
        ].join(''));
    };

    const handleProductsFetch = (requestParameters) => {
        try {
            get(LISTING_API_GET_LISTING_BY_NAVIGATION_ID, {
                ...requestParameters,
                navigationId: baseNavigationId,
                sub_navigation_id: navigationId
            })
                .then(response => batch(() => {
                    const {
                        paging,
                        availableFilters,
                        listingData,
                        seoData
                    } = response;
                    const { currentPage, pageCount, totalProducts } = paging;

                    onProductsChange(listingData);

                    if (seoData) {
                        applySeoData({
                            seoData,
                            paging,
                            requestParameters,
                            isNewListingsCanonicalTagFeatureActive,
                            isNextPrevAttributesFeatureActive
                        });
                    }

                    dispatch(setProducts(listingData));
                    dispatch(setSeoData(seoData));
                    dispatch(storeAvailableFilters(availableFilters));
                    dispatch(updatePagination(currentPage, pageCount));
                    dispatch(updateProductsCount(totalProducts));

                    if (locationChangeFromPopStateRef.current) {
                        locationChangeFromPopStateRef.current = false;
                    } else {
                        updateUrl(requestParameters, seoData);
                    }
                }));
        } catch (error) {}
    };

    useEffect(() => {
        if (firstCall.current) {
            firstCall.current = false;
        } else {
            handleProductsFetch(query);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    useEffect(() => {
        if (locationChangeRef.current) {
            locationChangeRef.current = false;
        } else if (navigationType === 'POP') {
            locationChangeFromPopStateRef.current = true;
            dispatch(setQueryFromUrl(getQueryFromUrl(limit)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, navigationType]);

    return null;
}
